import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import Footer from '../../components/layout/Footer';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

const BackLink = styled(Link)`
  position: absolute;
  top: 2rem;
  left: 2rem;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  font-size: 1rem;
  z-index: 10;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateX(-5px);
  }
  
  &::before {
    content: '←';
    margin-right: 8px;
    font-size: 1.2rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  text-align: center;
  color: #0d9488;
`;

const Description = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  color: #e0e0e0;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin: 4rem 0;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background-color: rgba(13, 148, 136, 0.1);
  padding: 2rem;
  border-radius: 8px;
  border: 1px solid #0d9488;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
`;

const ServiceImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 1.5rem;
`;

const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #0d9488;
  font-style: italic;
`;

const ServiceDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #e0e0e0;
`;

const CTASection = styled.div`
  margin-top: 6rem;
  text-align: center;
`;

const Maintenance: React.FC = () => {
  const { language } = useLanguage();
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };

  const services = [
    {
      image: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      title: language === 'it' ? 'PULIZIA STRAORDINARIA' : 'EXTRAORDINARY CLEANING',
      description: language === 'it' 
        ? 'Programmare servizi regolari di pulizia straordinaria è essenziale per mantenere uno standard elevato.'
        : 'Scheduling regular extraordinary cleaning services is essential to maintain a high standard.'
    },
    {
      image: 'https://images.unsplash.com/photo-1581579186913-45ac3e6efe93?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      title: language === 'it' ? 'CONTROLLO E RIPARAZIONE DEGLI ELETTRODOMESTICI' : 'CONTROL AND REPAIR OF APPLIANCES',
      description: language === 'it' 
        ? 'Verificare periodicamente lo stato degli elettrodomestici è importante per identificare eventuali malfunzionamenti.'
        : 'Periodically checking the status of appliances is important to identify any malfunctions.'
    },
    {
      image: 'https://images.unsplash.com/photo-1581579438747-14c8ab8e8faa?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      title: language === 'it' ? 'ISPEZIONE DELLE STRUTTURE' : 'INSPECTION OF STRUCTURES',
      description: language === 'it' 
        ? 'Verificare regolarmente gli impianti idraulici ed elettrici per identificare e risolvere problemi di manutenzione preventiva.'
        : 'Regularly check hydraulic and electrical systems to identify and solve preventive maintenance problems.'
    },
    {
      image: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      title: language === 'it' ? 'INTERVENTI PROFESSIONALI' : 'PROFESSIONAL INTERVENTIONS',
      description: language === 'it' 
        ? 'Agire velocemente le strutture della proprietà, incluso tetto, pavimenti e pareti, per identificare e risolvere problemi di manutenzione preventiva.'
        : 'Quickly act on property structures, including roof, floors and walls, to identify and solve preventive maintenance problems.'
    },
    {
      image: 'https://images.unsplash.com/photo-1556911220-bda9da8a1f2b?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      title: language === 'it' ? 'GESTIONE DEL GIARDINO E DELLO SPAZIO ESTERNO' : 'GARDEN AND OUTDOOR SPACE MANAGEMENT',
      description: language === 'it' 
        ? 'Spazi esterni, come giardini o cortili, è importante mantenerli in buono stato. Potatura delle piante, pulizia degli spazi esterni e gestione di eventuali problemi relativi all\'ambiente esterno.'
        : 'External spaces, such as gardens or courtyards, it is important to keep them in good condition. Pruning plants, cleaning outdoor spaces and managing any problems related to the external environment.'
    },
    {
      image: 'https://images.unsplash.com/photo-1581579420607-ba31c6d68538?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      title: language === 'it' ? 'ASSISTENZA TECNICA RAPIDA' : 'QUICK TECHNICAL ASSISTANCE',
      description: language === 'it' 
        ? 'Avere un sistema efficiente per gestire le segnalazioni di problemi da parte degli ospiti rispondendo tempestivamente a eventuali richieste di assistenza tecnica è fondamentale.'
        : 'Having an efficient system to manage problem reports from guests by responding promptly to any requests for technical assistance is fundamental.'
    },
    {
      image: 'https://images.unsplash.com/photo-1581579422864-71eb467dee20?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      title: language === 'it' ? 'FORNITURA DI BENI DI CONSUMO' : 'SUPPLY OF CONSUMER GOODS',
      description: language === 'it' 
        ? 'Garantire la fornitura costante di beni di consumo essenziali come carta igienica, sapone, detersivi e altri articoli necessari per il comfort degli ospiti.'
        : 'Ensure the constant supply of essential consumer goods such as toilet paper, soap, detergents and other items necessary for the comfort of guests.'
    },
    {
      image: 'https://images.unsplash.com/photo-1581578731355-ab25575ed7ae?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      title: language === 'it' ? 'CONFORMITÀ LEGALE E SICUREZZA' : 'LEGAL COMPLIANCE AND SAFETY',
      description: language === 'it' 
        ? 'Assicurarsi che la proprietà sia conforme a tutte le normative di sicurezza locali, comprese le ispezioni regolari dei rilevatori di fumo e monossido di carbonio.'
        : 'Ensure that the property complies with all local safety regulations, including regular inspections of smoke and carbon monoxide detectors.'
    },
    {
      image: 'https://images.unsplash.com/photo-1556909212-d5b604d0c90d?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      title: language === 'it' ? 'CONTROLLO E RIPARAZIONE DEGLI ARREDI' : 'CONTROL AND REPAIR OF FURNITURE',
      description: language === 'it' 
        ? 'Verificare regolarmente lo stato dei mobili e delle decorazioni, riparando o sostituendo gli elementi danneggiati per mantenere l\'attrattiva estetica della proprietà.'
        : 'Regularly check the condition of furniture and decorations, repairing or replacing damaged elements to maintain the aesthetic appeal of the property.'
    }
  ];
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Manutenzione | J-O-H' : 'Maintenance | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Servizio di manutenzione professionale per immobili in affitto breve, garantendo strutture efficienti e sicure per gli ospiti' 
            : 'Professional maintenance service for short-term rental properties, ensuring efficient and safe structures for guests'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <BackLink to="/services/property-owner">
          {language === 'it' ? 'Indietro' : 'Back'}
        </BackLink>
        <HeroContent>
          <PageTitle>MANUTENZIONE</PageTitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ContentContainer>
          <Description>
            {language === 'it' 
              ? 'La manutenzione degli immobili destinati agli affiti brevi è un aspetto cruciale per garantire che le proprietà rimangano attraenti, funzionali e sicure per gli ospiti. La natura temporanea degli affitti brevi richiede un\'attenzione particolare alla manutenzione per assicurare esperienze positive agli ospiti e la redditività dell\'attività. La manutenzione regolare è un investimento che paga dividendi nel mantenere una reputazione positiva e attirare ospiti futuri, oltre a preservare il valore della proprietà e a ridurre al minimo i rischi legali.'
              : 'The maintenance of properties intended for short-term rentals is a crucial aspect to ensure that properties remain attractive, functional and safe for guests. The temporary nature of short-term rentals requires special attention to maintenance to ensure positive experiences for guests and business profitability. Regular maintenance is an investment that pays dividends in maintaining a positive reputation and attracting future guests, as well as preserving the value of the property and minimizing legal risks.'}
          </Description>
          
          <ServicesGrid>
            {services.map((service, index) => (
              <ServiceCard key={index}>
                <ServiceImage src={service.image} alt={service.title} />
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
              </ServiceCard>
            ))}
          </ServicesGrid>
          
          <CTASection>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Richiedi un Preventivo' : 'Request a Quote'}
            </Button>
          </CTASection>
        </ContentContainer>
      </ContentSection>
      
      <Footer />
    </PageContainer>
  );
};

export default Maintenance; 